/**
 * Member-app copy of the slot encounter typing.
 *
 * Kept in step with apps/doctors/src/utils/slotTypes.ts and the server twin
 * functions/lib/slotTypes.js. The member needs it to filter the booking grid:
 * two staff roles now publish into one collection — Care Clinicians publish
 * intake interviews, seniors publish quarterly reviews — and the member's
 * booking screen must only ever offer the former.
 */
/**
 * Appointment typing for availability slots.
 *
 * Two fields, deliberately:
 *   - `encounterType` is the care-pathway discriminator and the only thing UI
 *     should branch on.
 *   - `serviceType` is the older field the Cloud Function already branches on
 *     (`functions/index.js`: anything other than 'clinical_history' is treated
 *     as a quarterly-report delivery call, which arms a sign-off deadline).
 *
 * Until the function is switched over to `encounterType`, the mapping below is
 * chosen so the existing check stays correct: an intake interview writes
 * 'clinical_history' and arms nothing; a quarterly review writes
 * 'quarterly_review' and correctly arms the delivery gate. That is why only
 * these two encounter types are offered today — a third value would fall into
 * the delivery branch by default, which is the bug this replaces.
 */

export type EncounterType = 'intake_interview' | 'quarterly_review';

export const SERVICE_TYPE_BY_ENCOUNTER: Record<EncounterType, string> = {
  intake_interview: 'clinical_history',
  quarterly_review: 'quarterly_review',
};

export const ENCOUNTER_LABELS: Record<EncounterType, string> = {
  intake_interview: 'Clinical history interview',
  quarterly_review: 'Quarterly review call',
};

/** Short form for the calendar cells, where space is tight. */
export const ENCOUNTER_SHORT_LABELS: Record<EncounterType, string> = {
  intake_interview: 'Intake',
  quarterly_review: 'Review',
};

export const ENCOUNTER_TYPES: EncounterType[] = ['intake_interview', 'quarterly_review'];

/**
 * The encounter type of an existing slot.
 *
 * Slots written before typing existed carry only `serviceType`. Those map to
 * an intake interview, which is what both the doctors' availability page
 * ('doctor_consultation') and the intake seeds ('clinical_history') were
 * actually publishing. Nothing legacy is ever inferred as a quarterly review —
 * guessing that would arm a sign-off deadline on a report from a slot that was
 * never a delivery call.
 */
export function resolveEncounterType(slot: {
  encounterType?: string | null;
  serviceType?: string | null;
}): EncounterType {
  if (slot.encounterType === 'quarterly_review') return 'quarterly_review';
  if (slot.encounterType === 'intake_interview') return 'intake_interview';
  if (slot.serviceType === 'quarterly_review') return 'quarterly_review';
  return 'intake_interview';
}
